import React, { useEffect, useMemo, Fragment, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BuildingIcon, MapPinIcon } from 'lucide-react';
// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
interface Location {
  name: string;
  type: 'client' | 'quad_service_center';
  address: string;
  description: string;
  coordinates?: [number, number];
}
interface LocationMapProps {
  locations: Location[];
}
// Simple coordinate lookup for common US cities and states
const CITY_COORDINATES: Record<string, [number, number]> = {
  // Major US Cities
  'New York, NY': [40.7128, -74.006],
  'Los Angeles, CA': [34.0522, -118.2437],
  'Chicago, IL': [41.8781, -87.6298],
  'Houston, TX': [29.7604, -95.3698],
  'Phoenix, AZ': [33.4484, -112.074],
  'Philadelphia, PA': [39.9526, -75.1652],
  'San Antonio, TX': [29.4241, -98.4936],
  'San Diego, CA': [32.7157, -117.1611],
  'Dallas, TX': [32.7767, -96.797],
  'San Jose, CA': [37.3382, -121.8863],
  'Austin, TX': [30.2672, -97.7431],
  'Jacksonville, FL': [30.3322, -81.6557],
  'Fort Worth, TX': [32.7555, -97.3308],
  'Columbus, OH': [39.9612, -82.9988],
  'Charlotte, NC': [35.2271, -80.8431],
  'San Francisco, CA': [37.7749, -122.4194],
  'Indianapolis, IN': [39.7684, -86.1581],
  'Seattle, WA': [47.6062, -122.3321],
  'Denver, CO': [39.7392, -104.9903],
  'Boston, MA': [42.3601, -71.0589],
  'Portland, OR': [45.5152, -122.6784],
  'Atlanta, GA': [33.749, -84.388],
  'Miami, FL': [25.7617, -80.1918],
  'Minneapolis, MN': [44.9778, -93.265],
  'Detroit, MI': [42.3314, -83.0458],
  'Nashville, TN': [36.1627, -86.7816],
  'Memphis, TN': [35.1495, -90.049],
  'Louisville, KY': [38.2527, -85.7585],
  'Baltimore, MD': [39.2904, -76.6122],
  'Milwaukee, WI': [43.0389, -87.9065],
  // International
  'London, UK': [51.5074, -0.1278],
  Singapore: [1.3521, 103.8198],
  'Toronto, Canada': [43.6532, -79.3832],
  'Sydney, Australia': [-33.8688, 151.2093],
  'Tokyo, Japan': [35.6762, 139.6503]
};
// Custom marker icons
const createCustomIcon = (color: string, icon: 'building' | 'pin') => {
  const iconSvg = icon === 'building' ? `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>` : `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>`;
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: ${color};
        width: 40px;
        height: 40px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="transform: rotate(45deg);">
          ${iconSvg}
        </div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};
const clientIcon = createCustomIcon('#3b82f6', 'building');
const quadIcon = createCustomIcon('#9333ea', 'pin');
// Calculate distance between two coordinates (Haversine formula)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
// Geocode location string to coordinates
function geocodeLocation(address: string): [number, number] | null {
  // Direct lookup
  if (CITY_COORDINATES[address]) {
    return CITY_COORDINATES[address];
  }
  // Try partial matches
  for (const [key, coords] of Object.entries(CITY_COORDINATES)) {
    if (address.includes(key) || key.includes(address)) {
      return coords;
    }
  }
  // Default to New York if no match found
  return CITY_COORDINATES['New York, NY'];
}
// Component to auto-fit bounds
function MapBounds({
  locations
}: {
  locations: Array<{
    coordinates: [number, number];
  }>;
}) {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => loc.coordinates));
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 10
      });
    }
  }, [locations, map]);
  return null;
}
export function LocationMap({
  locations
}: LocationMapProps) {
  // Geocode all locations
  const geocodedLocations = useMemo(() => {
    return locations.map(loc => ({
      ...loc,
      coordinates: geocodeLocation(loc.address) || [40.7128, -74.006] as [number, number]
    }));
  }, [locations]);
  const clientLocations = geocodedLocations.filter(loc => loc.type === 'client');
  const quadLocations = geocodedLocations.filter(loc => loc.type === 'quad_service_center');
  // Calculate distances from each client to nearest Quad facility
  const locationsWithDistances = useMemo(() => {
    return clientLocations.map(client => {
      const distances = quadLocations.map(quad => ({
        facility: quad.name,
        distance: calculateDistance(client.coordinates![0], client.coordinates![1], quad.coordinates![0], quad.coordinates![1])
      }));
      distances.sort((a, b) => a.distance - b.distance);
      return {
        ...client,
        nearestFacility: distances[0]
      };
    });
  }, [clientLocations, quadLocations]);
  return <div className="relative w-full h-[300px] rounded-xl overflow-hidden border border-slate-800/80 shadow-xl" data-id="element-791">
      <MapContainer center={geocodedLocations[0]?.coordinates || [40.7128, -74.006]} zoom={6} scrollWheelZoom={false} style={{
      height: '100%',
      width: '100%'
    }} className="z-0" data-id="element-792">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-793" />
        <MapBounds locations={geocodedLocations} data-id="element-794" />
        {/* Client Markers */}
        {locationsWithDistances.map((location, index) => <Marker key={`client-${index}`} position={location.coordinates!} icon={clientIcon} data-id="element-795">
            <Popup className="custom-popup" data-id="element-796">
              <div className="p-2 min-w-[250px]" data-id="element-797">
                <div className="flex items-center gap-2 mb-2" data-id="element-798">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center" data-id="element-799">
                    <BuildingIcon className="w-4 h-4 text-blue-400" data-id="element-800" />
                  </div>
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-bold rounded" data-id="element-801">
                    Client
                  </span>
                </div>
                <h3 className="font-bold text-sm mb-1" data-id="element-802">{location.name}</h3>
                <p className="text-xs text-slate-600 mb-2" data-id="element-803">
                  {location.address}
                </p>
                <p className="text-xs text-slate-700 mb-3" data-id="element-804">
                  {location.description}
                </p>
                {location.nearestFacility && <div className="pt-2 border-t border-slate-200" data-id="element-805">
                    <p className="text-xs font-semibold text-slate-700 mb-1" data-id="element-806">
                      Nearest Service Center:
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-807">
                      {location.nearestFacility.facility}
                    </p>
                    <p className="text-xs text-green-600 font-bold mt-1" data-id="element-808">
                      {Math.round(location.nearestFacility.distance)} miles away
                    </p>
                  </div>}
              </div>
            </Popup>
          </Marker>)}
        {/* Quad Service Center Markers with Coverage Circles */}
        {quadLocations.map((location, index) => <Fragment key={`quad-${index}`} data-id="element-809">
            <Circle center={location.coordinates!} radius={80467} // 50 miles in meters
        pathOptions={{
          color: '#9333ea',
          fillColor: '#9333ea',
          fillOpacity: 0.1,
          weight: 2,
          dashArray: '10, 10'
        }} data-id="element-810" />
            <Marker position={location.coordinates!} icon={quadIcon} data-id="element-811">
              <Popup className="custom-popup" data-id="element-812">
                <div className="p-2 min-w-[250px]" data-id="element-813">
                  <div className="flex items-center gap-2 mb-2" data-id="element-814">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center" data-id="element-815">
                      <MapPinIcon className="w-4 h-4 text-purple-400" data-id="element-816" />
                    </div>
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-bold rounded" data-id="element-817">
                      Quad Facility
                    </span>
                  </div>
                  <h3 className="font-bold text-sm mb-1" data-id="element-818">{location.name}</h3>
                  <p className="text-xs text-slate-600 mb-2" data-id="element-819">
                    {location.address}
                  </p>
                  <p className="text-xs text-slate-700 mb-3" data-id="element-820">
                    {location.description}
                  </p>
                  <div className="pt-2 border-t border-slate-200" data-id="element-821">
                    <p className="text-xs text-purple-600 font-semibold" data-id="element-822">
                      50-mile service radius
                    </p>
                  </div>
                </div>
              </Popup>
            </Marker>
          </Fragment>)}
      </MapContainer>
      {/* Map Legend - More Compact */}
      <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 p-3 z-[1000]" data-id="element-823">
        <div className="space-y-1.5" data-id="element-824">
          <div className="flex items-center gap-2" data-id="element-825">
            <div className="w-4 h-4 rounded-full bg-blue-500 border border-white shadow" data-id="element-826" />
            <span className="text-xs text-slate-700 font-medium" data-id="element-827">Client</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-828">
            <div className="w-4 h-4 rounded-full bg-purple-600 border border-white shadow" data-id="element-829" />
            <span className="text-xs text-slate-700 font-medium" data-id="element-830">Quad</span>
          </div>
        </div>
      </div>
    </div>;
}